import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { menuItems, MenuItem } from '../../components/Layout/menuItems';
import { Shield, Save, Plus, Trash2, Check, X, ChevronRight } from 'lucide-react';

// Função para obter o ID completo do menu/submenu
const getMenuId = (menu: MenuItem, submenu?: MenuItem) => {
  if (!menu.id) return menu.path || '';
  return submenu ? submenu.path || '' : menu.id;
};

interface Role {
  id: string;
  name: string;
  menuAccess: string[];
  isEditing?: boolean;
  isNew?: boolean;
}

interface EditModalProps {
  role: Role;
  onSave: (menuAccess: string[]) => void;
  onClose: () => void;
}

const EditModal: React.FC<EditModalProps> = ({ role, onSave, onClose }) => {
  const [selectedMenus, setSelectedMenus] = useState<string[]>(role.menuAccess || []);

  const toggleMenu = (menu: MenuItem, submenu?: MenuItem) => {
    const menuId = getMenuId(menu, submenu);
    
    setSelectedMenus(prev => {
      // Se já está selecionado, remove
      if (prev.includes(menuId)) {
        // Se for menu principal e não for submenu, remove todos os submenus
        if (!submenu && menu.subItems) {
          const subMenuIds = menu.subItems.map(sub => getMenuId(menu, sub));
          return prev.filter(id => !subMenuIds.includes(id) && id !== menuId);
        }
        // Se for submenu ou menu sem submenus, remove apenas ele
        return prev.filter(id => id !== menuId);
      }
      
      // Se não está selecionado
      if (submenu) {
        // Se for submenu, adiciona apenas ele
        return [...prev, menuId];
      } else {
        // Se for menu principal, adiciona apenas ele
        return [...prev, menuId];
      }
    });
  };

  const toggleAll = () => {
    if (selectedMenus.length === getAllMenuIds().length) {
      setSelectedMenus([]);
    } else {
      setSelectedMenus(getAllMenuIds());
    }
  };

  const getAllMenuIds = () => {
    const ids: string[] = [];
    menuItems.forEach(menu => {
      ids.push(menu.id);
      if (menu.subItems) {
        menu.subItems.forEach(sub => {
          ids.push(getMenuId(menu, sub));
        });
      }
    });
    return ids;
  };

  const isSubmenuSelected = (menu: MenuItem, submenu: MenuItem) => {
    return selectedMenus.includes(getMenuId(menu, submenu));
  };

  const areAllSubmenusSelected = (menu: MenuItem) => {
    if (!menu.subItems) return false;
    return menu.subItems.every(submenu => isSubmenuSelected(menu, submenu));
  };

  const isMenuSelected = (menu: MenuItem, submenu?: MenuItem) => {
    const menuId = getMenuId(menu, submenu);
    return selectedMenus.includes(menuId);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold">Editar Permissões - {role.name}</h2>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(80vh-200px)]">
          <div className="space-y-4">
            <div className="flex items-center space-x-2 mb-4 pb-4 border-b">
              <input
                type="checkbox"
                checked={selectedMenus.length === getAllMenuIds().length}
                onChange={toggleAll}
                className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
              />
              <span className="font-medium">Selecionar Todos</span>
            </div>
            {menuItems.map(menu => (
              <div key={menu.id} className="border rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <input
                    type="checkbox"
                    checked={isMenuSelected(menu)}
                    onChange={() => toggleMenu(menu)}
                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                  />
                  <span className="font-medium">{menu.label}</span>
                </div>

                {menu.subItems && menu.subItems.length > 0 && (
                  <div key={`${menu.id}-subItems`} className="ml-6 space-y-2 mt-2 border-l pl-4">
                    {menu.subItems.map(submenu => (
                      <div key={`${menu.id}-${submenu.id}`} className="flex items-center space-x-2">
                        <ChevronRight size={12} className="text-gray-400" />
                        <input
                          type="checkbox"
                          checked={isMenuSelected(menu, submenu)}
                          onChange={() => toggleMenu(menu, submenu)}
                          className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                        />
                        <span>{submenu.label}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 flex justify-end space-x-2">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            onClick={() => onSave(selectedMenus)}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
          >
            Guardar
          </button>
        </div>
      </div>
    </div>
  );
};

export default function UserRoles() {
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editingRole, setEditingRole] = useState<Role | null>(null);

  useEffect(() => {
    loadRoles();
  }, []);

  // Funções padrão do sistema
  const defaultRoles = [
    {
      id: 'funcionario',
      name: 'Funcionário',
      menuAccess: [],
      isEditing: false
    },
    {
      id: 'gerente',
      name: 'Gerente',
      menuAccess: [],
      isEditing: false
    },
    {
      id: 'admin',
      name: 'Administrador',
      menuAccess: menuItems.map(item => item.id), // Admin tem acesso a tudo por padrão
      isEditing: false
    }
  ] as Role[];

  const loadRoles = async () => {
    try {
      const rolesSnapshot = await getDocs(collection(db, 'roles'));
      const existingRoles = rolesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        menuAccess: doc.data().menuAccess || [], // Garantir que menuAccess é sempre um array
        isEditing: false
      })) as Role[];

      // Se não existirem funções, criar as funções padrão
      if (existingRoles.length === 0) {
        await Promise.all(defaultRoles.map(role => 
          setDoc(doc(db, 'roles', role.id), {
            name: role.name,
            menuAccess: role.menuAccess
          })
        ));
        setRoles(defaultRoles);
      } else {
        // Verificar se todas as funções padrão existem
        const missingRoles = defaultRoles.filter(defaultRole => 
          !existingRoles.some(existingRole => existingRole.id === defaultRole.id)
        );

        // Criar funções padrão que faltam
        if (missingRoles.length > 0) {
          await Promise.all(missingRoles.map(role => 
            setDoc(doc(db, 'roles', role.id), {
              name: role.name,
              menuAccess: role.menuAccess
            })
          ));
          
          // Adicionar as funções que faltavam à lista existente
          const updatedRoles = [
            ...existingRoles,
            ...missingRoles.map(role => ({ ...role, isEditing: false }))
          ];
          setRoles(updatedRoles);
        } else {
          setRoles(existingRoles);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar funções:', error);
      setError('Erro ao carregar funções. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const addNewRole = () => {
    const newRole: Role = {
      id: '',
      name: '',
      menuAccess: [],
      isEditing: true,
      isNew: true
    };
    setRoles(prev => [...prev, newRole]);
  };

  const deleteRole = async (roleId: string) => {
    // Verificar se é uma função padrão
    if (defaultRoles.some(role => role.id === roleId)) {
      setError('Não é possível eliminar funções padrão do sistema.');
      return;
    }

    try {
      await deleteDoc(doc(db, 'roles', roleId));
      setRoles(prev => prev.filter(role => role.id !== roleId));
      setError(''); // Limpar erro se existir
    } catch (error) {
      console.error('Erro ao eliminar função:', error);
      setError('Erro ao eliminar função. Por favor, tente novamente.');
    }
  };

  const handleRoleNameChange = (index: number, name: string) => {
    setRoles(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], name };
      return updated;
    });
  };

  const toggleEditMode = (index: number) => {
    setRoles(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], isEditing: !updated[index].isEditing };
      return updated;
    });
  };

  const saveRole = async (role: Role, index: number) => {
    try {
      if (!role.name) {
        setError('O nome da função é obrigatório.');
        return;
      }

      const roleId = role.isNew ? role.name.toLowerCase().replace(/\s+/g, '-') : role.id;
      await setDoc(doc(db, 'roles', roleId), {
        name: role.name,
        menuAccess: role.menuAccess || []
      }, { merge: true });

      setRoles(prev => {
        const updated = [...prev];
        updated[index] = { 
          ...updated[index], 
          id: roleId,
          isEditing: false,
          isNew: false
        };
        return updated;
      });

      setError('');
    } catch (error) {
      console.error('Erro ao guardar função:', error);
      setError('Erro ao guardar função. Por favor, tente novamente.');
    }
  };

  const handleSavePermissions = async (roleId: string, menuAccess: string[]) => {
    try {
      // Validar roleId
      if (!roleId) {
        throw new Error('ID da função é obrigatório');
      }

      // Encontrar a função
      const role = roles.find(r => r.id === roleId);
      if (!role) {
        throw new Error('Função não encontrada');
      }

      // Validar nome da função
      if (!role.name) {
        throw new Error('Nome da função é obrigatório');
      }

      // Garantir que menuAccess é sempre um array válido e sem valores undefined
      const safeMenuAccess = Array.isArray(menuAccess) 
        ? menuAccess.filter(item => item !== undefined && item !== null)
        : [];

      // Criar objeto com dados validados
      const roleData = {
        name: role.name.trim(),
        menuAccess: safeMenuAccess
      };

      // Verificar se há algum valor undefined
      Object.entries(roleData).forEach(([key, value]) => {
        if (value === undefined) {
          throw new Error(`Campo ${key} não pode ser undefined`);
        }
      });

      // Salvar no Firestore
      await setDoc(doc(db, 'roles', roleId), roleData);

      // Atualizar estado local
      setRoles(prev => prev.map(r => 
        r.id === roleId ? { ...r, menuAccess: safeMenuAccess } : r
      ));
      setEditingRole(null);
      setError('');
    } catch (error) {
      console.error('Erro ao guardar permissões:', error);
      setError('Erro ao guardar permissões. Por favor, tente novamente.');
    }
  };

  const saveRoles = async () => {
    setSaving(true);
    try {
      for (const role of roles) {
        await setDoc(doc(db, 'roles', role.id), {
          name: role.name,
          menuAccess: role.menuAccess || []
        });
      }
    } catch (error) {
      console.error('Erro ao guardar funções:', error);
    } finally {
      setSaving(false);
    }
  };

  const getAllMenuItems = (items: typeof menuItems): string[] => {
    const menuIds: string[] = [];
    items.forEach(item => {
      if (item.id) menuIds.push(item.id);
      if (item.subItems) {
        menuIds.push(...getAllMenuItems(item.subItems));
      }
    });
    return menuIds;
  };

  if (loading) {
    return <div>A carregar...</div>;
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <Shield className="w-6 h-6 text-primary" />
          <h1 className="text-2xl font-semibold">Função de Utilizadores</h1>
        </div>
        <button
          onClick={addNewRole}
          className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
        >
          <Plus size={16} />
          <span>Nova Função</span>
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Função
                </th>
                <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {roles.map((role, index) => (
                <tr key={role.id || index}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {role.isEditing ? (
                      <input
                        type="text"
                        value={role.name}
                        onChange={(e) => handleRoleNameChange(index, e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary"
                        placeholder="Nome da função"
                      />
                    ) : (
                      <span className="text-sm font-medium text-gray-900">{role.name}</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                    {role.isEditing ? (
                      <>
                        <button
                          onClick={() => saveRole(role, index)}
                          className="text-green-600 hover:text-green-900"
                          title="Guardar alterações"
                        >
                          <Check size={16} />
                        </button>
                        <button
                          onClick={() => role.isNew ? 
                            setRoles(prev => prev.filter((_, i) => i !== index)) : 
                            toggleEditMode(index)
                          }
                          className="text-red-600 hover:text-red-900"
                          title="Cancelar"
                        >
                          <X size={16} />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => setEditingRole(role)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Editar permissões"
                        >
                          <Save size={16} />
                        </button>
                        {/* Apenas mostrar botão de eliminar para funções não padrão */}
                        {!defaultRoles.some(defaultRole => defaultRole.id === role.id) && (
                          <button
                            onClick={() => deleteRole(role.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Eliminar função"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editingRole && (
        <EditModal
          role={editingRole}
          onSave={(menuAccess) => handleSavePermissions(editingRole.id, menuAccess)}
          onClose={() => setEditingRole(null)}
        />
      )}
    </div>
  );
}
